import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";

/**
 * Globaler Zeitraum-Context.
 *
 * Regeln (bestätigt durch User, 11.02.2026):
 * - Bei jedem Einstieg/Umstieg in einen Verlag wird das Jahr **immer** auf
 *   das jüngste Jahr mit tatsächlichen Bewegungsdaten gesetzt.
 * - Manuelle Jahresauswahl lebt **nur in-memory** und geht bei Reload/
 *   Verlagswechsel/Logout verloren.
 * - Fallback (keine Daten oder Request-Fehler): laufendes Kalenderjahr.
 *
 * Kein localStorage – alte Reste werden beim ersten Mount aufgeräumt.
 */
const PeriodContext = createContext(null);

const LEGACY_KEYS_PREFIX = "inventur:zeitraum";

const currentYear = () => String(new Date().getFullYear());

function cleanupLegacyKeys() {
    try {
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && (k === LEGACY_KEYS_PREFIX || k.startsWith(`${LEGACY_KEYS_PREFIX}:`))) {
                keys.push(k);
            }
        }
        keys.forEach((k) => localStorage.removeItem(k));
    } catch {
        /* ignore */
    }
}

export function PeriodProvider({ children }) {
    const { activeTenant } = useTenant() || {};
    const [zeitraum, setZeitraum] = useState(currentYear());

    // Altlasten (per-Tenant + Legacy-Key) einmalig entfernen.
    useEffect(() => {
        cleanupLegacyKeys();
    }, []);

    // Bei jedem Mandantenwechsel: jüngstes Jahr mit Daten laden und setzen.
    useEffect(() => {
        const tid = activeTenant?.tenant_id;
        if (!tid) return;

        let cancelled = false;
        (async () => {
            let year = currentYear();
            try {
                const r = await api.get(`/tenants/${tid}/latest-year`);
                const latest = r?.data?.latest_year;
                if (latest && /^\d{4}/.test(String(latest))) year = String(latest);
            } catch {
                /* Fallback: currentYear */
            }
            if (!cancelled) setZeitraum(year);
        })();
        return () => {
            cancelled = true;
        };
    }, [activeTenant?.tenant_id]);

    return (
        <PeriodContext.Provider value={{ zeitraum, setZeitraum }}>
            {children}
        </PeriodContext.Provider>
    );
}

export function usePeriod() {
    const ctx = useContext(PeriodContext);
    if (!ctx) throw new Error("usePeriod must be used within PeriodProvider");
    return ctx;
}
